import { useEffect, useState } from "react";
import axios from "axios";
import { PageShell } from "@/components/mai/PageShell";
import { useProduct } from "@/context/ProductContext";
import { CaretDown } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUS_CHIP = { ready: "bg-emerald-50 text-emerald-700 border-emerald-200", adaptation: "bg-amber-50 text-amber-700 border-amber-200", generate: "bg-red-50 text-red-700 border-red-200" };

const PlanCard = ({ p }) => {
  const [open, setOpen] = useState(false);
  return (
    <div data-testid={`submission-plan-${p.country_id}`} className="bg-white border border-slate-200">
      <button onClick={() => setOpen(!open)} className="w-full p-5 flex items-center gap-4 text-left">
        <span className="text-2xl">{p.flag}</span>
        <div className="flex-1 min-w-0">
          <p className="font-heading font-bold tracking-tight">{p.name}</p>
          <p className="text-xs text-slate-500">{p.readiness_band} · ~{p.estimated_months_to_decision} mo to decision · {p.preparation_burden.dossier_language}{p.preparation_burden.translation_required ? " (translation)" : ""}</p>
        </div>
        <div className="text-right">
          <p className="font-heading font-extrabold text-2xl">{p.submission_readiness_score}</p>
          <p className="text-[10px] text-slate-400 uppercase tracking-widest">Readiness</p>
        </div>
        <CaretDown size={16} className={`text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-5 pb-5 space-y-3 border-t border-slate-100 pt-4">
          <div className="grid grid-cols-2 gap-3 text-xs text-slate-600">
            <p>Mandatory coverage: <span className="font-mono-data font-semibold">{p.mandatory_coverage}%</span></p>
            <p>Preparation burden: <span className="font-mono-data font-semibold">{p.preparation_burden.score}/100</span></p>
          </div>
          <div>
            <p className="overline-label text-slate-400 mb-1.5">Minimum Evidence Package</p>
            <div className="border border-slate-200 divide-y divide-slate-100">
              {p.minimum_evidence_package.map((r) => (
                <div key={r.requirement_type} className="p-2.5 flex items-center gap-2 text-xs">
                  <span className={`shrink-0 uppercase tracking-wider text-[9px] font-semibold px-1.5 py-0.5 border ${STATUS_CHIP[r.status]}`}>{r.status}</span>
                  <span className="font-semibold text-slate-800">{r.requirement_type}</span>
                  <span className="text-slate-400 ml-auto truncate max-w-[45%]">{r.carrying_asset || "— to generate"}</span>
                </div>
              ))}
            </div>
          </div>
          {p.competitive_precedent && (
            <p data-testid={`precedent-note-${p.country_id}`} className={`text-xs border p-3 ${p.competitive_precedent.pathway_proven ? "bg-emerald-50/60 border-emerald-200 text-slate-700" : "bg-slate-50 border-slate-200 text-slate-600"}`}>
              <span className="font-semibold text-slate-800">Competitive precedent{p.competitive_precedent.pricing_anchor ? ` (anchor: ${p.competitive_precedent.pricing_anchor})` : ""}:</span> {p.competitive_precedent.note}
            </p>
          )}
          {p.scientific_advice_opportunity && (
            <p className="text-xs bg-slate-50 border border-slate-200 p-3 text-slate-600">
              <span className="font-semibold text-slate-800">Scientific advice ({p.scientific_advice_opportunity.timing}):</span> {p.scientific_advice_opportunity.provider} — {p.scientific_advice_opportunity.rationale}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default function SubmissionsPage() {
  const { productId } = useProduct();
  const [data, setData] = useState(null);
  useEffect(() => {
    setData(null);
    axios.get(`${API}/submission-engine/${productId}`).then((r) => setData(r.data)).catch(console.error);
  }, [productId]);

  return (
    <PageShell pageId="submissions" overline="Submission Intelligence Engine · Layer 9" title="Submission Planner">
      {!data ? (
        <div data-testid="loading-state" className="py-32 text-center text-slate-400 text-sm">Planning submissions…</div>
      ) : (
        <>
          <div data-testid="submission-summary" className="grid grid-cols-2 md:grid-cols-4 border border-slate-200 bg-white divide-x divide-y md:divide-y-0 divide-slate-200">
            {[["Wave 1 — Submit Now", data.summary.wave_1], ["Wave 2 — Fast Follow", data.summary.wave_2], ["Wave 3 — Evidence First", data.summary.wave_3], ["Avg Readiness", data.summary.avg_readiness]].map(([l, v]) => (
              <div key={l} className="p-6"><p className="overline-label text-slate-400">{l}</p><p className="font-heading font-extrabold text-4xl mt-3">{v}</p></div>
            ))}
          </div>

          {data.country_sequence.map((w) => (
            <section key={w.wave} className="space-y-3">
              <h2 className="font-heading font-bold text-lg tracking-tight">Wave {w.wave} — {w.label}</h2>
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                {w.countries.map((c) => {
                  const plan = data.submission_plans.find((p) => p.country_id === c.id);
                  return plan ? <PlanCard key={c.id} p={plan} /> : null;
                })}
              </div>
            </section>
          ))}

          <section className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            <div data-testid="parallel-clusters" className="bg-white border border-slate-200 p-6">
              <p className="overline-label text-slate-400">Parallel Submission Clusters</p>
              <div className="mt-4 space-y-4">
                {data.parallel_submissions.map((pc, i) => (
                  <div key={i} className="border-l-2 border-slate-900 pl-4">
                    <p className="text-sm font-semibold">Wave {pc.wave} · {pc.bucket_id}: {pc.countries.join(" + ")}</p>
                    <p className="text-xs text-slate-500 mt-1 leading-relaxed">{pc.rationale}</p>
                  </div>
                ))}
              </div>
            </div>
            <div data-testid="submission-dependencies" className="bg-white border border-slate-200 p-6">
              <p className="overline-label text-slate-400">Submission Dependencies</p>
              <div className="mt-4 space-y-3">
                {data.submission_dependencies.map((d, i) => (
                  <div key={i} className="text-xs">
                    <p className="font-semibold text-slate-800">{d.before} → {d.after} <span className="text-slate-400 font-normal">({d.type.replace(/_/g, " ")})</span></p>
                    <p className="text-slate-500 mt-0.5 leading-relaxed">{d.rationale}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </>
      )}
    </PageShell>
  );
}
